/**
 * API client configuration and initialization
 *
 * Configures the generated API client with base URL and interceptors.
 *
 * The client is automatically used by all generated SDK functions.
 * You can import individual functions from './generated' and they will
 * use this configured client with auth headers and error handling.
 */

import { client } from "./generated/client.gen";
import { getTokens, clearTokens } from "../utils/auth";
import { getApiUrl } from "../utils/getApiUrl";

// Configure the base client dynamically
// This ensures the API URL is correct even when accessed from different hosts (e.g., mobile)
function configureClient() {
  try {
    // Only configure if window is available
    if (typeof window === "undefined") {
      const fallbackUrl = "http://localhost:8000";
      client.setConfig({ baseUrl: fallbackUrl });
      return fallbackUrl;
    }

    const apiUrl = getApiUrl();
    client.setConfig({
      baseUrl: apiUrl,
    });
    console.log(
      "API client configured with base URL:",
      apiUrl,
      "from hostname:",
      window.location.hostname
    );
    return apiUrl;
  } catch (error) {
    console.error("Failed to configure API client:", error);
    // Fallback to localhost
    const fallbackUrl = "http://localhost:8000";
    client.setConfig({
      baseUrl: fallbackUrl,
    });
    console.warn("Using fallback API URL:", fallbackUrl);
    return fallbackUrl;
  }
}

// Track current API URL
let currentApiUrl: string | null = null;

// CSRF token storage
let csrfToken: string | null = null;

/**
 * Fetch CSRF token from the API by making a GET request
 * This ensures we have a token before making POST requests
 */
async function fetchCsrfToken(): Promise<void> {
  try {
    const apiUrl = typeof window !== "undefined" ? getApiUrl() : "http://localhost:8000";
    const response = await fetch(`${apiUrl}/health`, {
      method: "GET",
      credentials: "include",
    });
    const token = response.headers.get("X-CSRF-Token");
    if (token) {
      csrfToken = token;
      console.log("CSRF token fetched successfully");
    }
  } catch (error) {
    console.warn("Failed to fetch CSRF token:", error);
  }
}

// Configure immediately if window is available, otherwise wait
if (typeof window !== "undefined") {
  // Configure on DOMContentLoaded to ensure window.location is correct
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      currentApiUrl = configureClient();
      // Fetch CSRF token proactively
      fetchCsrfToken();
    });
  } else {
    currentApiUrl = configureClient();
    // Fetch CSRF token proactively
    fetchCsrfToken();
  }

  // Also reconfigure before each request to ensure correct URL
  // Always reconfigure to handle cases where the page loads before window.location is correct
  client.interceptors.request.use((request, options) => {
    // Always get fresh API URL to handle mobile/network access
    const apiUrl = getApiUrl();
    // Always update config to ensure correct URL (especially for mobile)
    client.setConfig({ baseUrl: apiUrl });
    if (apiUrl !== currentApiUrl) {
      console.log(
        "Reconfigured API client from",
        currentApiUrl,
        "to",
        apiUrl,
        "hostname:",
        window.location.hostname
      );
      currentApiUrl = apiUrl;
    }
    // Add Authorization header (but don't modify Content-Type for FormData requests)
    const { accessToken } = getTokens();
    if (accessToken) {
      // Check if this is a FormData request - if so, don't modify Content-Type
      const isFormData = request.body instanceof FormData;
      if (isFormData) {
        // For FormData, only add Authorization header if Content-Type is not explicitly null
        // The upload function sets Content-Type to null to let browser set boundary
        const contentType = request.headers.get("Content-Type");
        if (contentType !== null) {
          request.headers.set("Authorization", `Bearer ${accessToken}`);
        } else {
          // Content-Type is null (FormData), add Authorization without modifying Content-Type
          request.headers.set("Authorization", `Bearer ${accessToken}`);
        }
      } else {
        request.headers.set("Authorization", `Bearer ${accessToken}`);
      }
    }
    
    // Add CSRF token for state-changing requests (POST, PUT, PATCH, DELETE)
    if (request.method && ["POST", "PUT", "PATCH", "DELETE"].includes(request.method.toUpperCase())) {
      if (!csrfToken) {
        // If we don't have a token yet, log a warning
        // The token should be fetched on app load, but if it's missing,
        // the request will fail with 403 and the user will see an error
        console.warn("CSRF token missing for", request.method, "request to", request.url);
        console.warn("This may cause a 403 error. Token should be fetched from a GET request first.");
      } else {
        request.headers.set("X-CSRF-Token", csrfToken);
      }
    }
    
    return request;
  });
} else {
  // Fallback for non-browser environments
  currentApiUrl = configureClient();
  // Add Authorization header to all requests
  client.interceptors.request.use((request, options) => {
    const { accessToken } = getTokens();
    if (accessToken) {
      request.headers.set("Authorization", `Bearer ${accessToken}`);
    }
    
    // Add CSRF token for state-changing requests
    if (request.method && ["POST", "PUT", "PATCH", "DELETE"].includes(request.method.toUpperCase())) {
      if (csrfToken) {
        request.headers.set("X-CSRF-Token", csrfToken);
      }
    }
    
    return request;
  });
}

// Handle responses - extract CSRF token and handle 401/403 errors
client.interceptors.response.use((response, request, options) => {
  // Extract CSRF token from response headers (added by backend for GET/HEAD/OPTIONS)
  const csrfTokenHeader = response.headers.get("X-CSRF-Token");
  if (csrfTokenHeader) {
    csrfToken = csrfTokenHeader;
    console.log("CSRF token extracted from response");
  }
  
  if (response.status === 401) {
    // Clear tokens on authentication failure
    clearTokens();
    // Note: Login modal handling is done via useLoginModal in components
  }
  
  // Note: 403 errors might be CSRF-related, but we can't check the body here
  // without consuming it. The token will be fetched from the next GET request.
  
  return response;
});

// Export configured client
export { client };
